import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CalendarSearch, Filter, User, Clock, X } from 'lucide-react';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const slotColors = { available: 'bg-emerald-50 border-emerald-200', occupied: 'bg-red-50 border-red-200', empty: 'bg-slate-50 border-slate-100' };

function TrainerMultiSelect({ trainers, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const toggle = (val) => {
    const s = new Set(selected);
    if (s.has(val)) s.delete(val); else s.add(val);
    onChange(Array.from(s));
  };
  const label = selected.length === 0 ? 'All Trainers' : `${selected.length} trainer${selected.length > 1 ? 's' : ''}`;
  const filtered = searchTerm ? trainers.filter(t => t.name.toLowerCase().includes(searchTerm.toLowerCase())) : trainers;
  return (
    <div className="relative" data-testid="slot-filter-trainer">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 h-8 px-3 border rounded-sm text-xs text-slate-600 bg-white hover:bg-slate-50 min-w-[140px]">
        <User className="w-3 h-3 text-slate-400" />{label}
        {selected.length > 0 && <X className="w-3 h-3 ml-auto text-slate-400 hover:text-red-500" onClick={e => { e.stopPropagation(); onChange([]); }} />}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearchTerm(''); }} />
          <div className="absolute z-50 w-56 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
            <div className="p-1.5 border-b">
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search trainers..." className="w-full h-7 px-2 text-xs border rounded-sm outline-none focus:border-violet-300" autoFocus data-testid="slot-filter-trainer-search" />
            </div>
            <div className="overflow-y-auto max-h-52">
              {filtered.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-3">No matches</p>
              ) : filtered.map(t => (
                <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-violet-50 cursor-pointer text-xs">
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggle(t.id)} className="rounded border-slate-300" />
                  {t.name}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function SlotFinderPage() {
  const [grid, setGrid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [centres, setCentres] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [filters, setFilters] = useState({ course_id: '', centre_id: '', day: '', trainer_ids: [] });

  useEffect(() => {
    Promise.all([api.get('/training/courses'), api.get('/training/centres'), api.get('/training/trainers')]).then(([cR, ceR, tR]) => {
      setCourses(cR.data.courses || []);
      setCentres(ceR.data.centres || []);
      setTrainers((tR.data.trainers || []).filter(t => t.status === 'active'));
    });
  }, []);

  const fetchGrid = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.course_id) params.append('course_id', filters.course_id);
    if (filters.centre_id) params.append('centre_id', filters.centre_id);
    if (filters.day) params.append('day', filters.day);
    api.get(`/training/slot-finder?${params}`).then(r => setGrid(r.data.grid || [])).catch(() => {}).finally(() => setLoading(false));
  }, [filters.course_id, filters.centre_id, filters.day]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  // Filter grid by selected trainers (client-side)
  const filteredGrid = filters.trainer_ids.length === 0 ? grid : grid.map(dayData => ({
    ...dayData,
    slots: dayData.slots.map(slot => {
      const filteredAvailable = slot.available.filter(t => filters.trainer_ids.includes(t.trainer_id));
      const filteredOccupied = slot.occupied.filter(t => filters.trainer_ids.includes(t.trainer_id));
      if (filteredAvailable.length === 0 && filteredOccupied.length === 0) return null;
      return { ...slot, available: filteredAvailable, occupied: filteredOccupied, status: filteredAvailable.length > 0 ? 'available' : 'occupied' };
    }).filter(Boolean),
  })).filter(d => d.slots.length > 0);

  return (
    <div className="space-y-6" data-testid="slot-finder-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Slot Finder</h1>
        <p className="text-sm text-slate-400 mt-1">Find available training slots and trainers</p>
      </div>

      {/* Filters */}
      <Card className="border p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filters.course_id} onValueChange={v => setFilters(p => ({ ...p, course_id: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="slot-filter-course"><SelectValue placeholder="All Courses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.centre_id} onValueChange={v => setFilters(p => ({ ...p, centre_id: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="slot-filter-centre"><SelectValue placeholder="All Centres" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Centres</SelectItem>
              {centres.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.day} onValueChange={v => setFilters(p => ({ ...p, day: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="slot-filter-day"><SelectValue placeholder="All Days" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Days</SelectItem>
              {ALL_DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <TrainerMultiSelect
            trainers={trainers}
            selected={filters.trainer_ids}
            onChange={v => setFilters(p => ({ ...p, trainer_ids: v }))}
          />
        </div>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300"></div><span className="text-[10px] text-slate-500">Available</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-100 border border-red-300"></div><span className="text-[10px] text-slate-500">Occupied</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-slate-100 border border-slate-200"></div><span className="text-[10px] text-slate-500">No Trainers</span></div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="h-40 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div></div>
      ) : filteredGrid.length === 0 ? (
        <Card className="border p-12 text-center">
          <CalendarSearch className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">{filters.trainer_ids.length > 0 ? 'No slots found for selected trainers' : 'No schedule data available'}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredGrid.map(dayData => (
            <Card key={dayData.day} className="border p-0 overflow-hidden" data-testid={`slot-day-${dayData.day.toLowerCase()}`}>
              <div className="bg-slate-50 px-4 py-2.5 border-b">
                <h3 className="text-sm font-semibold text-slate-900">{dayData.day}</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3">
                {dayData.slots.map(slot => (
                  <div key={slot.time} className={`rounded-sm border p-2.5 ${slotColors[slot.status] || slotColors.empty}`} data-testid={`slot-${dayData.day.toLowerCase()}-${slot.time_start}`}>
                    <div className="flex items-center gap-1 mb-1.5">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] font-semibold text-slate-700">{slot.time}</span>
                    </div>
                    {slot.available.length > 0 && (
                      <div className="mb-1">
                        <p className="text-[9px] uppercase text-emerald-600 font-semibold mb-0.5">Available</p>
                        {slot.available.map(t => (
                          <div key={t.trainer_id} className="flex items-center gap-1">
                            <User className="w-2.5 h-2.5 text-emerald-500" />
                            <span className="text-[10px] text-emerald-700">{t.trainer_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {slot.occupied.length > 0 && (
                      <div>
                        <p className="text-[9px] uppercase text-red-500 font-semibold mb-0.5">Occupied</p>
                        {slot.occupied.map((o, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <User className="w-2.5 h-2.5 text-red-400" />
                            <span className="text-[10px] text-red-600">{o.trainer_name}</span>
                            <span className="text-[9px] text-red-400">({o.course_name})</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {slot.available.length === 0 && slot.occupied.length === 0 && (
                      <p className="text-[10px] text-slate-300">No trainers</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
